import type {
  TabProps as MantineTabProps,
  TabsProps as MantineTabsProps,
  TabsValue,
} from '@mantine/core';
import { Tabs as MantineTabs } from '@mantine/core';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { Text } from './Text';

export type TabsProps = {
  id?: string;
  grow?: boolean;
  onTabChange?: (value: TabsValue) => void;
  noRouterOnTabChange?: boolean;
  tabs?: ({
    'data-test-id'?: string;
    label: string | React.ReactNode;
  } & MantineTabProps)[];
  size?: 'primary' | 'secondary';
} & Omit<MantineTabsProps, 'children' | 'onTabChange'>;

// @TODO: This needs to be completely rewritten, it's impossible to re-style via `classNames`!
export function Tabs({
  id = 'activeTab',
  tabs,
  grow,
  onTabChange,
  noRouterOnTabChange,
  size = 'primary',
  ...props
}: TabsProps) {
  const router = useRouter();
  const { pathname, query } = router;
  const activeTabParam = query[id] as string;
  const initialValue = noRouterOnTabChange ? tabs[0]?.value : (activeTabParam || tabs[0]?.value);
  const [activeTab, setActiveTab] = useState(initialValue);

  useEffect(() => {
    if (noRouterOnTabChange) return;
    if (!activeTabParam && tabs[0]?.value) {
      const newQuery = { ...query, [id]: tabs[0]?.value };
      router.replace({ pathname, query: newQuery }, undefined, { shallow: true });
      setActiveTab(tabs[0]?.value);
    }
  }, [activeTabParam, id, noRouterOnTabChange, pathname, query, router, tabs]);

  const handleOnTabChange = (value) => {
    if (!noRouterOnTabChange) {
      router.replace({ pathname, query: { ...query, [id]: value } }, undefined, {
        shallow: true,
      });
    }
    setActiveTab(value);
    // this allows us to replace existing Tabs implementations easily
    if (onTabChange) {
      onTabChange(value);
    }
  };

  const getTabSx = useCallback(
    (isSelected: boolean) =>
      size === 'primary'
        ? {
            padding: /* eslint-disable-line local-rules/use-theme-tokens */ '16px 24px',
            '&:hover': {
              // eslint-disable-next-line local-rules/use-theme-tokens
              marginBottom: -1,
              // eslint-disable-next-line local-rules/use-theme-tokens
              paddingBottom: 15,
            },
            ...(isSelected
              ? {
                  /* eslint-disable local-rules/use-theme-tokens */
                  marginBottom: -1,
                  paddingBottom: 15,
                  /* eslint-enable local-rules/use-theme-tokens */
                }
              : {}),
          }
        : {
            padding: /* eslint-disable-line local-rules/use-theme-tokens */ '8px 16px',
            '&:hover': {
              // eslint-disable-next-line local-rules/use-theme-tokens
              marginBottom: -1,
              // eslint-disable-next-line local-rules/use-theme-tokens
              paddingBottom: 7,
            },
            ...(isSelected
              ? {
                  /* eslint-disable local-rules/use-theme-tokens */
                  marginBottom: -1,
                  paddingBottom: 7,
                  /* eslint-enable local-rules/use-theme-tokens */
                }
              : {}),
          },
    [size]
  );

  return (
    <MantineTabs
      value={activeTab}
      onTabChange={handleOnTabChange}
      {...props}
      styles={{
        tabsList: {
          gap: /* eslint-disable-line local-rules/use-theme-tokens */ size === 'primary' ? 24 : 8,
          borderBottomWidth: 1,
        },
      }}
    >
      <MantineTabs.List grow={grow} bg="white" /* eslint-disable-line local-rules/use-theme-tokens */ px={size === 'primary' ? '16px' : '0px'}>
        {tabs.map(({ label, value, ...tabProps }) => {
          const isSelected = activeTab === value;

          return (
            <MantineTabs.Tab
              data-test-id={props['data-test-id']}
              key={value}
              value={value}
              sx={getTabSx(isSelected)}
              {...tabProps}
              color="gray.7"
            >
              <Text
                color={isSelected ? 'gray.9' : 'gray.6'}
                variant={size === 'primary' ? 'headline-small' : 'subhead-small'}
              >
                {label}
              </Text>
            </MantineTabs.Tab>
          );
        })}
      </MantineTabs.List>
    </MantineTabs>
  );
}
